"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import ExcelJS from "exceljs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { fetchItemsAPI, updateItemAPI } from "../api/itemsApi";
import { validateCell, parseSelectOptions, type TableField } from "./CsvImportDialog";
import { parseImportTemporal, excelCellValueToString } from "@/utils/importTemporal";
import { buildRowFieldSummaryLine } from "./buildRowFieldSummary";
import {
  buildRelationLookup,
  fetchAllRelatedItems,
  resolveRelationValue,
  relationItemPickerLabel,
} from "./relationImportLookup";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

const EMPTY = "__skip__";
/** Sentinel override meaning "exclude this row from the update". */
const SKIP_ROW = "__skip_row__";

/** 0-based column index to spreadsheet letter (0 -> A, 26 -> AA). */
function colLetter(index: number): string {
  let n = index;
  let s = "";
  do {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return s;
}

async function fetchAllCollectionItems(slug: string): Promise<Record<string, unknown>[]> {
  const all: Record<string, unknown>[] = [];
  let page = 1;
  // Must match GET /collections/:slug/items max page size for list (non-relate); server caps at 100.
  // Using a higher limit caused batch.length < limit after the first response (100 < 200) and stopped pagination early.
  const pageSize = 100;
  for (; ;) {
    const { items, totalCount } = await fetchItemsAPI(slug, { page, limit: pageSize });
    const batch = Array.isArray(items) ? items : [];
    all.push(...batch);
    if (batch.length === 0) break;
    if (typeof totalCount === "number" && all.length >= totalCount) break;
    if (batch.length < pageSize) break;
    page += 1;
  }
  return all;
}

function normalizeMatchKey(raw: string, field: TableField): string {
  const s = String(raw).trim();
  if (!s) return "";
  switch (field.type) {
    case "number": {
      const n = Number(s);
      return Number.isNaN(n) ? s : String(n);
    }
    case "timeonly": {
      const d = parseImportTemporal(s, { timeOnly: true });
      return d ? d.toISOString() : s;
    }
    case "datetime":
    case "dateonly":
    case "calendar": {
      const d = parseImportTemporal(s);
      return d ? d.toISOString() : s;
    }
    case "checkbox": {
      const lower = s.toLowerCase();
      if (["1", "true", "yes", "on"].includes(lower)) return "true";
      if (["0", "false", "no", "off"].includes(lower)) return "false";
      return s;
    }
    default:
      return s;
  }
}

function normalizeStoredKey(value: unknown, field: TableField): string {
  if (value == null || value === "") return "";
  switch (field.type) {
    case "number":
      return String(Number(value as number));
    case "timeonly": {
      const d = parseImportTemporal(value, { timeOnly: true });
      return d ? d.toISOString() : String(value).trim();
    }
    case "datetime":
    case "dateonly":
    case "calendar": {
      const d = parseImportTemporal(value);
      return d ? d.toISOString() : String(value).trim();
    }
    case "checkbox":
      return value === true || value === "true" || value === 1 ? "true" : "false";
    case "relation":
    case "systemrelation":
    case "multiplerelation":
      if (typeof value === "object" && value !== null && "uid" in value) {
        return String((value as { uid: unknown }).uid).trim();
      }
      return String(value).trim();
    default:
      return String(value).trim();
  }
}

export type ExcelBulkUpdateRow = {
  sheetRow: number;
  whereRaw: string;
  valueRaw: string;
  valid: boolean;
  /** User chose to exclude this row from the update. */
  skipped: boolean;
  /** Match value problem (empty/no match/multiple/duplicate) — only fixable in the file, not the table. */
  whereProblem: boolean;
  messages: string[];
  targetUid: string | null;
  /** Short preview of the matched collection row (first fields by order). */
  targetRowSummary: string;
  resolvedValue: unknown;
  resolvedDisplay: string;
};

type ExcelBulkUpdateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  fields: TableField[];
  onApplied: () => void;
};

export function ExcelBulkUpdateDialog({
  open,
  onOpenChange,
  slug,
  fields,
  onApplied,
}: ExcelBulkUpdateDialogProps) {
  const [fileName, setFileName] = useState("");
  const [parsedRows, setParsedRows] = useState<{ sheetRow: number; cells: string[] }[]>([]);
  const [columnCount, setColumnCount] = useState(0);
  const [matchColIndex, setMatchColIndex] = useState(0);
  const [valueColIndex, setValueColIndex] = useState(1);
  const [firstRowIsHeader, setFirstRowIsHeader] = useState(false);
  // Per-row manual value picks (keyed by sheetRow) for invalid rows on option/relation fields.
  const [overrides, setOverrides] = useState<Record<number, string>>({});
  // Wizard step: field/column mapping, then the validation preview.
  const [step, setStep] = useState<"select" | "preview">("select");
  // Bumped to reshuffle the random example rows shown in the summary box.
  const [exampleSeed, setExampleSeed] = useState(0);
  const [whereFieldName, setWhereFieldName] = useState<string>(EMPTY);
  const [valueFieldName, setValueFieldName] = useState<string>(EMPTY);
  const [collectionItems, setCollectionItems] = useState<Record<string, unknown>[] | null>(null);
  const [collectionLoadError, setCollectionLoadError] = useState<string | null>(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [relatedItems, setRelatedItems] = useState<Record<string, unknown>[] | null>(null);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [relatedError, setRelatedError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);

  const candidateWhereFields = useMemo(
    () => fields.filter((f) => !["image", "file", "instruction"].includes(f.type)),
    [fields]
  );

  const candidateValueFields = useMemo(
    () => fields.filter((f) => !["image", "file", "instruction"].includes(f.type)),
    [fields]
  );

  const whereField = useMemo(
    () => candidateWhereFields.find((f) => f.name === whereFieldName) ?? null,
    [candidateWhereFields, whereFieldName]
  );

  const valueField = useMemo(
    () => candidateValueFields.find((f) => f.name === valueFieldName) ?? null,
    [candidateValueFields, valueFieldName]
  );

  useEffect(() => {
    if (!open || !slug) return;
    setLoadingItems(true);
    setCollectionLoadError(null);
    fetchAllCollectionItems(slug)
      .then(setCollectionItems)
      .catch((e) => {
        setCollectionItems(null);
        setCollectionLoadError(e instanceof Error ? e.message : "Failed to load collection items");
      })
      .finally(() => setLoadingItems(false));
  }, [open, slug]);

  useEffect(() => {
    if (!open || !valueField) {
      setRelatedItems(null);
      setRelatedError(null);
      return;
    }
    const t = valueField.type;
    if (t !== "relation" && t !== "systemrelation" && t !== "multiplerelation") {
      setRelatedItems(null);
      setRelatedError(null);
      return;
    }
    const key = typeof valueField.value === "string" ? valueField.value : "";
    if (!key) {
      setRelatedItems([]);
      setRelatedError("Related collection is not configured for this field.");
      return;
    }
    setLoadingRelated(true);
    setRelatedError(null);
    fetchAllRelatedItems(key, t)
      .then((items) => {
        setRelatedItems(items);
        if (items.length === 0) {
          setRelatedError("No related rows were returned (check permissions or data).");
        }
      })
      .catch((e) => {
        setRelatedItems(null);
        setRelatedError(e instanceof Error ? e.message : "Failed to load related items");
      })
      .finally(() => setLoadingRelated(false));
  }, [open, valueField]);

  const handleFile = useCallback(async (file: File | null) => {
    setFileName("");
    setParsedRows([]);
    setColumnCount(0);
    setOverrides({});
    setStep("select");
    if (!file) return;
    setFileName(file.name);
    const buf = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buf);
    const sheet = workbook.worksheets[0];
    if (!sheet) {
      setParsedRows([]);
      return;
    }
    const colCount = Math.max(sheet.columnCount, 2);
    const out: { sheetRow: number; cells: string[] }[] = [];
    sheet.eachRow((row, rowNumber) => {
      const cells: string[] = [];
      for (let c = 1; c <= colCount; c++) {
        cells.push(excelCellValueToString(row.getCell(c)));
      }
      if (cells.every((x) => x === "")) return;
      out.push({ sheetRow: rowNumber, cells });
    });
    setColumnCount(colCount);
    setMatchColIndex(0);
    setValueColIndex(colCount > 1 ? 1 : 0);
    setParsedRows(out);
  }, []);

  const dataRows = useMemo(() => {
    if (!firstRowIsHeader || parsedRows.length === 0) return parsedRows;
    return parsedRows.slice(1);
  }, [parsedRows, firstRowIsHeader]);

  const headerCells = useMemo(
    () => (firstRowIsHeader ? parsedRows[0]?.cells ?? [] : []),
    [firstRowIsHeader, parsedRows]
  );

  const columnOptions = useMemo(() => {
    const opts: { index: number; label: string }[] = [];
    for (let i = 0; i < columnCount; i++) {
      const header = headerCells[i]?.trim();
      opts.push({ index: i, label: header ? `${colLetter(i)} — ${header}` : colLetter(i) });
    }
    return opts;
  }, [columnCount, headerCells]);

  // Suggest the collection field whose label/name matches a source column's header.
  const suggestFieldName = useCallback(
    (colIndex: number, candidates: TableField[]): string | null => {
      const h = headerCells[colIndex]?.trim().toLowerCase();
      if (!h) return null;
      const f = candidates.find(
        (c) => (c.label || c.name).toLowerCase() === h || c.name.toLowerCase() === h
      );
      return f ? f.name : null;
    },
    [headerCells]
  );

  // When a header row is present, auto-fill the field for each column if not chosen yet.
  useEffect(() => {
    if (!firstRowIsHeader) return;
    if (whereFieldName === EMPTY) {
      const f = suggestFieldName(matchColIndex, candidateWhereFields);
      if (f) setWhereFieldName(f);
    }
    if (valueFieldName === EMPTY) {
      const f = suggestFieldName(valueColIndex, candidateValueFields);
      if (f) setValueFieldName(f);
    }
  }, [
    firstRowIsHeader,
    suggestFieldName,
    matchColIndex,
    valueColIndex,
    candidateWhereFields,
    candidateValueFields,
    whereFieldName,
    valueFieldName,
  ]);

  // Picking a source column re-suggests its field (overrides a prior suggestion).
  const handleMatchColChange = (v: string) => {
    const idx = Number(v);
    setMatchColIndex(idx);
    const f = suggestFieldName(idx, candidateWhereFields);
    if (f) setWhereFieldName(f);
  };
  const handleValueColChange = (v: string) => {
    const idx = Number(v);
    setValueColIndex(idx);
    const f = suggestFieldName(idx, candidateValueFields);
    if (f) setValueFieldName(f);
  };
  const clearOverride = (sheetRow: number) =>
    setOverrides((prev) => {
      const next = { ...prev };
      delete next[sheetRow];
      return next;
    });

  const matchColumnLabel =
    columnOptions.find((c) => c.index === matchColIndex)?.label ?? colLetter(matchColIndex);
  const valueColumnLabel =
    columnOptions.find((c) => c.index === valueColIndex)?.label ?? colLetter(valueColIndex);

  const relationLookup = useMemo(() => {
    if (!valueField) return null;
    const t = valueField.type;
    if (t !== "relation" && t !== "systemrelation" && t !== "multiplerelation") return null;
    return buildRelationLookup(relatedItems ?? [], valueField);
  }, [valueField, relatedItems]);

  // Selectable values for the value field, used to offer an inline picker on invalid rows.
  const valueOptions = useMemo((): { label: string; value: string }[] => {
    if (!valueField) return [];
    const t = valueField.type;
    if (t === "select" || t === "dropdown") {
      return parseSelectOptions(valueField.options).map((o) => ({ label: o, value: o }));
    }
    if (t === "relation" || t === "systemrelation" || t === "multiplerelation") {
      return (relatedItems ?? [])
        .map((it) => {
          const uid = it.uid ?? it.id;
          if (uid == null) return null;
          return { label: relationItemPickerLabel(it, valueField), value: String(uid) };
        })
        .filter((x): x is { label: string; value: string } => x !== null);
    }
    return [];
  }, [valueField, relatedItems]);

  const valueFieldHasOptions = valueOptions.length > 0;

  // Reset manual picks whenever the value field or its source column changes.
  useEffect(() => {
    setOverrides({});
  }, [valueFieldName, valueColIndex]);

  const whereIndex = useMemo(() => {
    const m = new Map<string, string[]>();
    if (!whereField || !collectionItems) return m;
    for (const item of collectionItems) {
      const uid = item.uid != null ? String(item.uid) : null;
      if (!uid) continue;
      const key = normalizeStoredKey(item[whereField.name], whereField);
      if (!key) continue;
      const list = m.get(key) ?? [];
      list.push(uid);
      m.set(key, list);
    }
    return m;
  }, [whereField, collectionItems]);

  const evaluatedRows = useMemo((): ExcelBulkUpdateRow[] => {
    if (!whereField || !valueField || !collectionItems || collectionItems.length === 0) {
      return [];
    }
    const keyCounts = new Map<string, number>();
    for (const r of dataRows) {
      const nk = normalizeMatchKey(r.cells[matchColIndex] ?? "", whereField);
      if (!nk) continue;
      keyCounts.set(nk, (keyCounts.get(nk) ?? 0) + 1);
    }

    const rows: ExcelBulkUpdateRow[] = [];

    for (const r of dataRows) {
      const messages: string[] = [];
      const whereRaw = r.cells[matchColIndex] ?? "";
      const override = overrides[r.sheetRow];

      if (override === SKIP_ROW) {
        rows.push({
          sheetRow: r.sheetRow,
          whereRaw,
          valueRaw: r.cells[valueColIndex] ?? "",
          valid: false,
          skipped: true,
          whereProblem: false,
          messages: ["Skipped — won't be updated"],
          targetUid: null,
          targetRowSummary: "—",
          resolvedValue: undefined,
          resolvedDisplay: "",
        });
        continue;
      }

      const valueRaw = override != null ? override : (r.cells[valueColIndex] ?? "");

      if (!String(whereRaw).trim()) {
        messages.push("Where value is empty");
        rows.push({
          sheetRow: r.sheetRow,
          whereRaw,
          valueRaw,
          valid: false,
          skipped: false,
          whereProblem: true,
          messages,
          targetUid: null,
          targetRowSummary: "—",
          resolvedValue: undefined,
          resolvedDisplay: "",
        });
        continue;
      }

      let whereProblem = false;
      const normWhere = normalizeMatchKey(whereRaw, whereField);
      if ((keyCounts.get(normWhere) ?? 0) > 1) {
        messages.push("This where value appears in more than one row in the file");
        whereProblem = true;
      }

      const candidates = whereIndex.get(normWhere);
      if (!candidates || candidates.length === 0) {
        messages.push("No item in this collection matches the where value");
        whereProblem = true;
      } else if (candidates.length > 1) {
        messages.push("Multiple items match this where value; match must be unique");
        whereProblem = true;
      }

      let targetUid: string | null = candidates?.length === 1 ? candidates[0] : null;

      const targetItem =
        targetUid != null
          ? collectionItems.find((it) => String(it.uid) === targetUid)
          : undefined;
      const targetRowSummary = buildRowFieldSummaryLine(targetItem, fields);

      let resolvedValue: unknown;
      let resolvedDisplay = "";
      const ve = validateCell(valueRaw, valueField, r.sheetRow);
      if (ve.error) {
        messages.push(`${ve.error.fieldName}: ${ve.error.message}`);
      }

      const baseType = valueField.type;
      if (
        baseType === "relation" ||
        baseType === "systemrelation" ||
        baseType === "multiplerelation"
      ) {
        if (loadingRelated) {
          messages.push("Still loading related collection data for validation");
        } else if (relatedError) {
          messages.push(relatedError);
        } else if (!relationLookup) {
          messages.push("Related data is not ready");
        } else {
          const { uid, message } = resolveRelationValue(valueRaw, relationLookup);
          if (message) messages.push(message);
          if (uid) {
            resolvedValue = baseType === "multiplerelation" ? [uid] : uid;
            resolvedDisplay = uid;
          }
        }
      } else {
        if (ve.value !== undefined && ve.value !== null && !ve.error) {
          resolvedValue = ve.value;
          resolvedDisplay =
            ve.value instanceof Date ? ve.value.toISOString() : String(ve.value);
        } else if (!String(valueRaw).trim()) {
          messages.push("Value is empty");
        } else if (!ve.error && ve.value === null) {
          messages.push("Value is empty or could not be parsed");
        }
      }

      const valid = messages.length === 0 && targetUid != null;
      rows.push({
        sheetRow: r.sheetRow,
        whereRaw,
        valueRaw,
        valid,
        skipped: false,
        whereProblem,
        messages,
        targetUid,
        targetRowSummary,
        resolvedValue,
        resolvedDisplay,
      });
    }

    return rows;
  }, [
    whereField,
    valueField,
    fields,
    collectionItems,
    dataRows,
    matchColIndex,
    valueColIndex,
    overrides,
    whereIndex,
    relationLookup,
    loadingRelated,
    relatedError,
  ]);

  // Up to 3 random valid rows, shown as concrete examples in the summary box.
  const exampleRows = useMemo(() => {
    void exampleSeed; // re-roll trigger
    const valids = evaluatedRows.filter((r) => r.valid);
    return [...valids].sort(() => Math.random() - 0.5).slice(0, 3);
  }, [evaluatedRows, exampleSeed]);

  const validCount = evaluatedRows.filter((r) => r.valid).length;
  const skippedCount = evaluatedRows.filter((r) => r.skipped).length;
  const invalidCount = evaluatedRows.length - validCount - skippedCount;
  // Rows that can only be fixed by editing the file (match value doesn't resolve uniquely).
  const hasWhereProblem = evaluatedRows.some((r) => !r.valid && !r.skipped && r.whereProblem);
  const canApply =
    evaluatedRows.length > 0 &&
    invalidCount === 0 &&
    validCount > 0 &&
    !loadingItems &&
    !collectionLoadError &&
    !loadingRelated &&
    !relatedError &&
    whereFieldName !== EMPTY &&
    valueFieldName !== EMPTY &&
    whereFieldName !== valueFieldName;

  // Enough mapping to build and show the preview.
  const canProceed =
    parsedRows.length > 0 &&
    dataRows.length > 0 &&
    whereFieldName !== EMPTY &&
    valueFieldName !== EMPTY &&
    whereFieldName !== valueFieldName &&
    !loadingItems &&
    !collectionLoadError &&
    !!collectionItems;

  const resetOnClose = useCallback(() => {
    setStep("select");
    setFileName("");
    setParsedRows([]);
    setColumnCount(0);
    setMatchColIndex(0);
    setValueColIndex(1);
    setOverrides({});
    setFirstRowIsHeader(false);
    setWhereFieldName(EMPTY);
    setValueFieldName(EMPTY);
    setCollectionItems(null);
    setCollectionLoadError(null);
    setRelatedItems(null);
    setRelatedError(null);
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) resetOnClose();
    onOpenChange(next);
  };

  const handleApply = async () => {
    if (!canApply || !valueField) return;
    setApplying(true);
    try {
      for (const row of evaluatedRows) {
        if (row.skipped || !row.valid || !row.targetUid) continue;
        await updateItemAPI(slug, row.targetUid, { [valueField.name]: row.resolvedValue });
      }
      onApplied();
      handleOpenChange(false);
    } catch (e) {
      console.error("Excel bulk update failed:", e);
      alert(e instanceof Error ? e.message : "Update failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "select" ? "Import Excel updates — map columns" : "Import Excel updates — preview"}
          </DialogTitle>
          <DialogDescription className="space-y-1.5">
            {step === "select" ? (
              <>
                <span className="block">
                  Pick the spreadsheet column that identifies the row (the match value) and the column holding the new field value — any columns, not just the first two.
                </span>
                <span className="block">
                  Then choose which collection field each maps to, and continue to preview the changes.
                </span>
              </>
            ) : (
              <span className="block">
                Review the rows below. Invalid rows show a picker: choose a valid value inline (for option or relation fields), or skip the row to leave it out. All rows must be valid or skipped to apply.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {step === "select" && (
            <>
          <div className="space-y-2">
            <Label htmlFor="excel-update-file">Excel file (.xlsx)</Label>
            <InputFile id="excel-update-file" onFile={handleFile} disabled={loadingItems} />
            {fileName ? (
              <p className="text-xs text-muted-foreground">Loaded: {fileName}</p>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="excel-header-row"
              checked={firstRowIsHeader}
              onCheckedChange={(c) => setFirstRowIsHeader(Boolean(c))}
            />
            <Label htmlFor="excel-header-row" className="text-sm font-normal cursor-pointer">
              First row is a header (skip it)
            </Label>
          </div>

          {parsedRows.length > 0 && columnCount > 0 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Match column (spreadsheet)</Label>
                <Select value={String(matchColIndex)} onValueChange={handleMatchColChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOptions.map((c) => (
                      <SelectItem key={c.index} value={String(c.index)}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value column (spreadsheet)</Label>
                <Select value={String(valueColIndex)} onValueChange={handleValueColChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOptions.map((c) => (
                      <SelectItem key={c.index} value={String(c.index)}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>
                {`Match field (${whereField ? (whereField.label || whereField.name) : "e.g. code"})`}
              </Label>
              <Select value={whereFieldName} onValueChange={setWhereFieldName}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY}>Select…</SelectItem>
                  {candidateWhereFields.map((f) => (
                    <SelectItem key={f.name} value={f.name}>
                      {f.label || f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Value field (update)</Label>
              <Select value={valueFieldName} onValueChange={setValueFieldName}>
                <SelectTrigger>
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY}>Select…</SelectItem>
                  {candidateValueFields.map((f) => (
                    <SelectItem key={f.name} value={f.name}>
                      {f.label || f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {whereFieldName !== EMPTY &&
            valueFieldName !== EMPTY &&
            whereFieldName === valueFieldName && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                Choose two different fields for where and value.
              </div>
            )}

          {whereField &&
            valueField &&
            whereFieldName !== valueFieldName &&
            parsedRows.length > 0 && (
              <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-sm text-blue-700 dark:text-blue-300">
                <div>
                  Set <strong>{valueField.label || valueField.name}</strong> to{" "}
                  <strong>{valueColumnLabel}</strong> where{" "}
                  <strong>{whereField.label || whereField.name}</strong> equals{" "}
                  <strong>{matchColumnLabel}</strong>.
                </div>
                {exampleRows.length > 0 && (
                  <div className="mt-2 space-y-0.5 text-xs text-blue-700/80 dark:text-blue-300/80">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Examples:</span>
                      {validCount > 3 && (
                        <button
                          type="button"
                          className="underline hover:no-underline"
                          onClick={() => setExampleSeed((s) => s + 1)}
                        >
                          show another 3
                        </button>
                      )}
                    </div>
                    {exampleRows.map((r) => (
                      <div key={r.sheetRow}>
                        Set {valueField.label || valueField.name} to “
                        {r.valueRaw || "—"}” where {whereField.label || whereField.name}{" "}
                        equals “{r.whereRaw}”.
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          {loadingItems && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading items for matching…
            </div>
          )}
          {collectionLoadError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex gap-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              {collectionLoadError}
            </div>
          )}
          {!loadingItems && collectionItems && (
            <p className="text-xs text-muted-foreground">
              Loaded {collectionItems.length} item(s) from this collection for matching.
            </p>
          )}

          {valueField &&
            (valueField.type === "relation" ||
              valueField.type === "systemrelation" ||
              valueField.type === "multiplerelation") && (
              <>
                {loadingRelated && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading related collection for validation…
                  </div>
                )}
                {relatedError && (
                  <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive flex gap-2">
                    <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                    {relatedError}
                  </div>
                )}
                {!loadingRelated && relatedItems && !relatedError && (
                  <p className="text-xs text-muted-foreground">
                    Loaded {relatedItems.length} related row(s) for value validation.
                  </p>
                )}
              </>
            )}
            </>
          )}

          {step === "preview" && evaluatedRows.length > 0 && (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span>
                  Total: <strong>{evaluatedRows.length}</strong>
                </span>
                <span className="text-green-700 dark:text-green-400">
                  Valid: <strong>{validCount}</strong>
                </span>
                {skippedCount > 0 && (
                  <span className="text-muted-foreground">
                    Skipped: <strong>{skippedCount}</strong>
                  </span>
                )}
                <span className={invalidCount > 0 ? "text-destructive" : ""}>
                  Invalid: <strong>{invalidCount}</strong>
                </span>
              </div>
              {invalidCount === 0 ? (
                <div className="rounded-md border border-green-500/30 bg-green-500/10 p-3 flex items-center gap-2 text-green-700 dark:text-green-400 text-sm">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  {skippedCount > 0
                    ? `Every row is valid or skipped. You can apply ${validCount} update(s).`
                    : "All rows are valid. You can apply updates."}
                </div>
              ) : (
                <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 flex items-start gap-2 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                  {hasWhereProblem
                    ? "Some rows' match values don't match a single item in this collection. Fix those values in your file (or skip the rows) and re-upload — they can't be corrected in this table."
                    : "Fix invalid rows, pick a value, or skip them before applying. Apply stays disabled until every row is valid or skipped."}
                </div>
              )}

              <Label>Preview of updates</Label>
              <div className="rounded-md border overflow-x-auto max-h-[min(50vh,420px)] overflow-y-auto text-xs">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-muted/50 sticky top-0 z-10">
                      <th className="border p-2 text-left font-medium">Row</th>
                      <th className="border p-2 text-left font-medium">Status</th>
                      <th className="border p-2 text-left font-medium">
                        Where ({whereField ? (whereField.label || whereField.name) : "column A"})
                      </th>
                      <th className="border p-2 text-left font-medium">Matching row</th>
                      <th className="border p-2 text-left font-medium">
                        Value ({valueField ? (valueField.label || valueField.name) : "column B"})
                      </th>
                      <th className="border p-2 text-left font-medium">Resolved value</th>
                      <th className="border p-2 text-left font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {evaluatedRows.map((row) => (
                      <tr
                        key={row.sheetRow}
                        className={
                          row.skipped
                            ? "bg-muted/40 text-muted-foreground"
                            : row.valid
                              ? "bg-background"
                              : "bg-destructive/5"
                        }
                      >
                        <td className="border p-2 whitespace-nowrap">{row.sheetRow}</td>
                        <td className="border p-2 whitespace-nowrap">
                          {row.skipped ? "Skipped" : row.valid ? "Valid" : "Invalid"}
                        </td>
                        <td className="border p-2 max-w-[140px] truncate" title={row.whereRaw}>
                          {row.whereRaw}
                        </td>
                        <td
                          className="border p-2 text-[11px] max-w-[280px] truncate"
                          title={
                            row.targetUid
                              ? `${row.targetRowSummary}\n(uid: ${row.targetUid})`
                              : row.targetRowSummary
                          }
                        >
                          {row.targetRowSummary}
                        </td>
                        <td className="border p-2 max-w-[140px] truncate" title={row.valueRaw}>
                          {row.valueRaw}
                        </td>
                        <td className="border p-2 max-w-[200px]">
                          {row.skipped ? (
                            <span className="text-xs">
                              Skipped{" "}
                              <button
                                type="button"
                                className="underline hover:no-underline"
                                onClick={() => clearOverride(row.sheetRow)}
                              >
                                undo
                              </button>
                            </span>
                          ) : row.whereProblem ? (
                            <button
                              type="button"
                              className="text-xs underline hover:no-underline text-destructive"
                              onClick={() =>
                                setOverrides((prev) => ({ ...prev, [row.sheetRow]: SKIP_ROW }))
                              }
                            >
                              Skip row
                            </button>
                          ) : !row.valid || overrides[row.sheetRow] != null ? (
                            <Select
                              value={overrides[row.sheetRow]}
                              onValueChange={(v) =>
                                setOverrides((prev) => ({ ...prev, [row.sheetRow]: v }))
                              }
                            >
                              <SelectTrigger className="h-7 text-xs">
                                <SelectValue
                                  placeholder={valueFieldHasOptions ? "Pick a value…" : "Skip?"}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {valueOptions.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                                <SelectItem value={SKIP_ROW}>Skip this row</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="truncate block" title={row.resolvedDisplay}>
                              {row.resolvedDisplay || "—"}
                            </span>
                          )}
                        </td>
                        <td className="border p-2 text-destructive/90 max-w-[220px]" title={row.messages.join(" ")}>
                          {row.messages.length ? row.messages.join("; ") : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {step === "select" && parsedRows.length > 0 && dataRows.length === 0 && (
            <p className="text-sm text-muted-foreground">No data rows after skipping the header.</p>
          )}
        </div>

        <DialogFooter className="sm:justify-start gap-2">
          {step === "select" ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={applying}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={() => setStep("preview")}
                disabled={!canProceed}
              >
                Next
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep("select")}
                disabled={applying}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={handleApply}
                disabled={!canApply || applying}
              >
                {applying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Applying…
                  </>
                ) : (
                  `Apply ${validCount} update(s)`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InputFile({
  id,
  onFile,
  disabled,
}: {
  id: string;
  onFile: (file: File | null) => void;
  disabled?: boolean;
}) {
  return (
    <input
      id={id}
      type="file"
      accept=".xlsx,.xlsm"
      disabled={disabled}
      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm file:mr-3 file:rounded file:border-0 file:bg-muted file:px-2 file:py-1 file:text-xs"
      onChange={(e) => {
        const f = e.target.files?.[0] ?? null;
        e.target.value = "";
        void onFile(f);
      }}
    />
  );
}
